import React from 'react';
import { WidgetProps } from '@rjsf/core';
import { DataJSON, DataService, SchemaEndpoint } from '../../services/DataService';
import { Button, Divider, Dropdown, Form, Grid, Icon, Loader, Message, Modal, Segment } from 'semantic-ui-react';
import { DropdownProps } from 'semantic-ui-react/dist/commonjs/modules/Dropdown/Dropdown';
import { GeneralContractorJSON } from '../GeneralContractorJSONSchema';
import GCSchemaForm from '../../pages/general-contractors/components/GCSchemaForm';
import { AlertService } from '../../services/AlertService';
import { JSONSchema7 } from 'json-schema';

// Define a clean option type for dropdown
interface DropdownOption {
  key: string;
  value: string;
  text: string;
}

interface RecordSelectorState {
  loading: boolean;
  selectedRecord: DataJSON | undefined;
  records: Array<DataJSON>;
  dropdownOptions: Array<DropdownOption>; // Add this for clean dropdown options
  isError: boolean;
  isModalOpened: boolean;
  isGCCreationFailed: boolean;
  isGCCreationSucceeded: boolean;
  generalContractor?: GeneralContractorJSON | undefined;
}

interface NewFeatureRecordSelectorProps extends WidgetProps {
  options: {
    alert: AlertService;
    selectedRecordId: string;
    endpoint: SchemaEndpoint;
    listFields: JSONSchema7;
    displayFieldName: string;
    displayFieldSchema: JSONSchema7;
    parent: DataJSON;
  };
}

// TODO: this duplicated code should be removed when we agree upon new feature.
export default class NewFeatureRecordSelector extends React.Component<NewFeatureRecordSelectorProps,
  RecordSelectorState> {
  state: RecordSelectorState = {
    isModalOpened: false,
    loading: true,
    selectedRecord: undefined,
    records: [],
    dropdownOptions: [], // Initialize clean options
    isError: false,
    isGCCreationFailed: false,
    isGCCreationSucceeded: false,
  };
  dataService = new DataService();

  async componentWillUnmount(): Promise<void> {
    // Clean up - no longer needed since we're not modifying original records
  }

  async componentDidMount(): Promise<void> {
    let records = await this.dataService.list(this.props.uiSchema.options.endpoint);
    if (!records || records.length === 0) {
      records = [];
    }

    // Create clean dropdown options without extra properties - filter out records without id
    const dropdownOptions: Array<DropdownOption> = records
      .filter(record => record.id) // Filter out records without id
      .map(record => ({
        key: record.id!, // Use non-null assertion since we filtered
        value: record.id!,
        text: String(record.name || '') // Handle potential undefined name
      }));

    // Sort the dropdown options
    dropdownOptions.sort((a, b) => a.text.localeCompare(b.text));

    const selectedGeneralContractor = this.props.options.selectedRecordId ?
      records.find(record => record.id === this.props.options.selectedRecordId)
      : undefined;

    this.setState({
      loading: false,
      records,
      dropdownOptions,
      selectedRecord: selectedGeneralContractor
    });
  }

  handleDropdownChange(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps): void {
    let selectedRecord: DataJSON | undefined = undefined;

    // Use the dropdown value directly instead of accessing event.currentTarget
    const selectedId = data.value as string;

    if (selectedId) {
      selectedRecord = this.state.records.find(record => record.id === selectedId);

      this.setState({
        selectedRecord: selectedRecord as DataJSON,
        isError: false
      });

      if (selectedRecord?.id) {
        this.props.onChange(selectedRecord.id);
      }
    } else {
      this.setState({
        isError: true,
        selectedRecord: undefined
      });
      this.props.onChange(selectedRecord);
    }
  }

  async performOnSuccess(record: DataJSON | undefined): Promise<void> {
    if (typeof record !== 'undefined' && record.id) { // Check that record.id exists
      this.props.options.alert.createAlert({
        message: 'Successfully created General Contractor',
        title: 'Success',
        type: 'success'
      });

      // Add the new record to both records and dropdown options
      const { records, dropdownOptions } = this.state;
      records.push(record);
      
      const newOption: DropdownOption = {
        key: record.id,
        value: record.id,
        text: String(record.name || '') // Handle potential undefined name
      };
      dropdownOptions.push(newOption);

      // Re-sort dropdown options
      dropdownOptions.sort((a, b) => a.text.localeCompare(b.text));

      this.setState({
        isModalOpened: false,
        isGCCreationSucceeded: true,
        records: records,
        dropdownOptions: dropdownOptions,
        selectedRecord: record as DataJSON,
        isError: false
      });

      this.props.onChange(record.id);
    }
  }

  async performOnError(): Promise<void> {
    this.props.options.alert.createAlert({
      message: 'Failed to create General Contractor. Please try again.',
      title: 'Error',
      type: 'error'
    });
    this.setState({
      isGCCreationFailed: true
    });
  }

  onModalClose(): void {
    this.setState({
      isModalOpened: false,
    });
  }

  render(): React.ReactNode {
    const { dropdownOptions, isModalOpened, isError, selectedRecord, loading } = this.state;
    const { required, label } = this.props;
    
    if (loading) {
      return (
        <Loader/>
      );
    }

    if (isModalOpened) {
      return (
        <Modal
          closeIcon
          onClose={(): void => this.onModalClose()}
          size='large'
          open={isModalOpened}
        >
          <Modal.Header>Create General Contractor</Modal.Header>
          <Modal.Content scrolling>
            <GCSchemaForm
              onCancelButtonClick={(): void => this.setState({ isModalOpened: false })}
              onSuccessfulGCCreationCallback={(record: DataJSON | undefined): Promise<void> => this.performOnSuccess(record)}
              onErrorGCCreationCallback={(): Promise<void> => this.performOnError()}
            />
          </Modal.Content>
        </Modal>
      );
    }

    return (
      <Form.Field required={required}>
        {
          dropdownOptions.length > 0 ?
            <div style={{ width: '60%' }}>
              <style>
                {`
                  /* Apply grey placeholder ONLY if dropdown is NOT in error */
                  #gc_select.ui.dropdown:not(.error) > .default.text {
                    color: #4d4d4d !important;
                  }
                `}
              </style>


              <label
                style={{ fontWeight: 'bold', fontSize: '13px' }}
                className={required ? 'requiredStar' : ''}
              >
                {label}
              </label>
              <Segment>
                <Grid columns={2} stackable style={{ textAlign: 'center' }}>
                  <Grid.Column>
                    <Dropdown
                      id='gc_select'
                      clearable
                      search
                      selection
                      error={isError}
                      style={{ width: '250px' }}
                      placeholder='Select an Existing Contractor'
                      onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps): void => this.handleDropdownChange(event, data)}
                      text={selectedRecord?.name as string || ''}
                      value={selectedRecord?.id as string || ''}
                      options={dropdownOptions} // Use clean options without extra properties
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <Button
                      primary
                      icon
                      labelPosition='left'
                      onClick={(): void => this.setState({ isModalOpened: true })}
                    >
                      <Icon name='user plus'/>
                      Create New General Contractor
                    </Button>
                  </Grid.Column>
                </Grid>
                <Divider vertical>Or</Divider>
              </Segment>
            </div>
            :
            <div>
              <label
                style={{ fontWeight: 'bold', fontSize: '13px' }}
                className={required ? 'requiredStar' : ''}
              >
                {label}
              </label>
              <Segment compact>
                <Button
                  primary
                  icon
                  labelPosition='left'
                  onClick={(): void => this.setState({ isModalOpened: true })}
                >
                  <Icon name='user plus'/>
                  Create New General Contractor
                </Button>
              </Segment>

            </div>
        }
      </Form.Field>
    );
  }
}